import { Link } from "react-router-dom";

import { longDateTime } from "../lib/format";
import { headlineAttribute, presentAttributes } from "../lib/manifest";
import type { Entry, Manifest } from "../lib/types";
import { AttributeTable } from "./AttributeTable";
import { ConfidenceNote } from "./ConfidenceNote";
import { TitleField } from "./FieldNote";

export type EntryDetailsProps = {
  entry: Entry;
  manifest: Manifest | undefined;
  className?: string;
};

/**
 * Everything about a find that isn't the photo, the headline facts, or your
 * own notes: Claude's longer field note, the full attribute sheet, tags,
 * timestamps, and the rename control. Closed by default — worth having, not
 * worth putting ahead of what you actually came here to see.
 */
export function EntryDetails({ entry, manifest, className }: EntryDetailsProps) {
  const headline = headlineAttribute(manifest, entry.domain, entry.attributes);
  const attributes = presentAttributes(manifest, entry.domain, entry.attributes).filter(
    (attribute) => attribute.key !== headline?.key,
  );

  return (
    <details className={className}>
      <summary className="touch inline-flex cursor-pointer list-none items-center gap-1.5 font-label text-sm text-ink-soft hover:text-ink [&::-webkit-details-marker]:hidden">
        <span aria-hidden="true" className="details-caret">
          ▸
        </span>
        More about this find
      </summary>

      <div className="sheet grain mt-3 space-y-6 p-4 sm:p-5">
        {entry.summary ? (
          <p className="max-w-prose font-prose leading-relaxed text-ink">{entry.summary}</p>
        ) : null}

        <TitleField entry={entry} />

        {attributes.length > 0 ? (
          <div>
            <h2 className="field-label mb-1 text-ink-soft">Record</h2>
            <AttributeTable
              manifest={manifest}
              domain={entry.domain}
              attributes={entry.attributes}
              excludeKeys={headline ? [headline.key] : undefined}
            />
          </div>
        ) : null}

        <ConfidenceNote confidence={entry.confidence} identificationNotes={entry.identificationNotes} />

        {entry.tags.length > 0 ? (
          <div>
            <h2 className="field-label mb-2 text-ink-soft">Tags</h2>
            <ul className="flex flex-wrap gap-2">
              {entry.tags.map((tag) => (
                <li key={tag}>
                  <Link
                    to={`/scrapbook?q=${encodeURIComponent(tag)}`}
                    className="inline-flex border border-rule bg-paper px-2 py-1 font-label text-xs tracking-wide text-ink-soft hover:border-ochre hover:text-ink"
                  >
                    {tag}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <dl className="border-t border-rule pt-3">
          <div className="flex flex-wrap items-baseline gap-x-3 py-1">
            <dt className="field-label-tight text-ink-soft">Added</dt>
            <dd className="font-prose text-sm text-ink">{longDateTime(entry.createdAt)}</dd>
          </div>
          {entry.capturedAt ? (
            <div className="flex flex-wrap items-baseline gap-x-3 py-1">
              <dt className="field-label-tight text-ink-soft">Photographed</dt>
              <dd className="font-prose text-sm text-ink">{longDateTime(entry.capturedAt)}</dd>
            </div>
          ) : null}
          {entry.classifiedAt ? (
            <div className="flex flex-wrap items-baseline gap-x-3 py-1">
              <dt className="field-label-tight text-ink-soft">Determined</dt>
              <dd className="font-prose text-sm text-ink">{longDateTime(entry.classifiedAt)}</dd>
            </div>
          ) : null}
        </dl>
      </div>
    </details>
  );
}
